// src/features/videos/FeaturedVideoHero.tsx — the Videos page's Featured hero, after the
// owner's concept (docs/References/Images/videos_page.webp, 2026-09-26): the poster fills
// the whole card, a "Most Featured" pill and the duration ride its top corners, a play
// button sits on the art, and the title block (category, big title with the last word in
// the brand gradient, why it's featured, creator, description, counts) lies over a dark
// scrim on the right. On phones the block moves to the bottom over a rising scrim.
//
// The card is always dark (it is a picture), so its text uses fixed light colours in both
// themes — the same rule as the other media overlays (FeaturedLive's chrome bar).
//
// The link is a layer over the whole card rather than the card itself, so the concept's
// ··· menu (the `menu` slot, at the end of the counts row) can be a real button beside it —
// a button inside a link is invalid and unreachable by keyboard.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { darkTheme } from '@/theme/tokens';
import { Avatar } from '@/ui';
import { Icon } from '@/icons';
import { GeneratedThumb } from '@/components/GeneratedThumb/GeneratedThumb';
import { formatCount } from '@/components/formatCount';
import type { VideoItem, VideoCreator } from '@/core/models/video';
import { formatDuration, formatViews } from './meta';

const INK = '#fff';
const INK_MUTED = 'rgba(255, 255, 255, 0.74)';

const Hero = styled.div`
  position: relative;
  isolation: isolate;
  /* Above the rows below it, so the ··· menu's popover (which opens inside the card) is not
     painted under the Trending cards. */
  z-index: 1;
  display: grid;
  /* Concept: the art owns the left, the text block the right ~46%. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 46%);
  /* A floor, not a fixed shape: the text block sets the height when it needs more, so a
     long title grows the card instead of being clipped (an aspect-ratio box with
     overflow:hidden would not grow). ~3.3:1 at a 1440px window, like the concept. */
  min-height: 320px;
  margin-bottom: 22px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  /* No overflow:hidden here. It would clip the ··· popover. The art clips itself. */
  background: #07050f;
  color: ${INK};
  /* No stroke change on hover (owner rule): the play button's grow is the hover cue. */

  ${media.mobile`
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
  `}
`;

const Art = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  overflow: hidden;

  /* The scrim the title block reads against: clear over the art, dark under the text. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(
        90deg,
        rgba(7, 5, 15, 0) 0%,
        rgba(7, 5, 15, 0.12) 38%,
        rgba(7, 5, 15, 0.78) 58%,
        rgba(7, 5, 15, 0.92) 100%
      ),
      linear-gradient(0deg, rgba(7, 5, 15, 0.45) 0%, rgba(7, 5, 15, 0) 35%);
    ${media.mobile`
      background: linear-gradient(
        0deg,
        rgba(7, 5, 15, 0.94) 0%,
        rgba(7, 5, 15, 0.86) 45%,
        rgba(7, 5, 15, 0) 75%
      );
    `}
  }
`;

/* The card's link: an empty layer over everything but the ··· menu, named by aria-label. */
const CardLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
`;

const Poster = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 35%;
`;

const Corner = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 14px;
  ${({ $side }) => ($side === 'left' ? 'left: 14px;' : 'right: 14px;')}
  display: flex;
  gap: 6px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(20, 12, 40, 0.62);
  backdrop-filter: blur(6px);
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${INK};
`;

const Duration = styled.span`
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: rgba(0, 0, 0, 0.62);
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  color: ${INK};
`;

/* Decorative: the card's link covers it. Sits on the art, just left of the text block. */
const PlayButton = styled.span`
  position: absolute;
  left: 40%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  place-items: center;
  width: 68px;
  height: 68px;
  padding-left: 4px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(20, 12, 40, 0.55);
  backdrop-filter: blur(6px);
  box-shadow: 0 0 28px ${darkTheme.color.brandTo}88;
  color: ${INK};
  transition: transform 160ms ease;
  ${Hero}:hover & {
    transform: translate(-50%, -50%) scale(1.06);
  }
  ${media.mobile`
    left: 50%;
    top: 32%;
    width: 56px;
    height: 56px;
  `}
`;

const Body = styled.div`
  grid-column: 2;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Top padding clears the corner pills when a tall block fills the card. */
  padding: 50px 28px 22px 8px;
  min-width: 0;

  ${media.mobile`
    grid-column: 1;
    padding: 46% 16px 16px;
  `}
`;

const Eyebrow = styled.span`
  font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${INK_MUTED};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(22px, 2.6vw, 40px);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: 0.01em;
  color: ${INK};
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* The concept's second title line: the brand gradient. Fixed dark-theme brand stops — the
   card is dark in both themes, and the light theme's deep purple would sink into it. */
const Accent = styled.span`
  background: linear-gradient(90deg, ${darkTheme.color.brandFrom}, ${darkTheme.color.brandTo});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Reason = styled.p`
  margin: 0;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${INK};
`;

const CreatorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
  min-width: 0;
`;

const CreatorText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const CreatorName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${INK};
`;

const Verified = styled.span`
  display: inline-flex;
  color: ${darkTheme.color.accentCyan};
`;

const Small = styled.span`
  display: flex;
  column-gap: 0.9em;
  font: 400 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${INK_MUTED};
  overflow: hidden;
  white-space: nowrap;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Blurb = styled.p`
  margin: 0;
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${INK_MUTED};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 2px;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${INK};
  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

/* Above the card's link so the ··· stays clickable; flush right like the concept. */
const MenuSlot = styled.div`
  position: relative;
  z-index: 2;
  margin-left: auto;
  /* Only the popover's trigger (its menu keeps the theme): the card is dark in both
     themes, and the theme's muted ink would vanish on it in light mode. */
  > div > button[aria-haspopup] {
    color: ${INK_MUTED};
    &:hover,
    &[aria-expanded='true'] {
      color: ${INK};
      background: rgba(255, 255, 255, 0.14);
    }
  }
`;

/** "A Brighter Tomorrow" → ["A Brighter", "Tomorrow"]: the last word takes the accent. */
function splitTitle(title: string): [string, string] {
  const words = title.trim().split(/\s+/);
  if (words.length < 2) return ['', title.trim()];
  const last = words.pop() ?? '';
  return [words.join(' '), last];
}

export interface FeaturedVideoHeroProps {
  video: VideoItem;
  creator?: VideoCreator;
  /** Why this video is featured — shown to everyone (owner: "a visible why line"). */
  reason?: string;
  /** The post's counts, when it is a real post. */
  likes?: number;
  comments?: number;
  /** The ··· menu (a real post's own), placed at the end of the counts row. */
  menu?: ReactNode;
}

export function FeaturedVideoHero({
  video,
  creator,
  reason,
  likes,
  comments,
  menu,
}: FeaturedVideoHeroProps) {
  const [lead, last] = splitTitle(video.title);
  return (
    <Hero data-testid="featured-hero">
      <CardLink to={`/videos/${video.id}`} aria-label={`Featured: ${video.title}`} />
      <Art>
        {video.posterUrl ? (
          <Poster
            src={video.posterUrl}
            alt=""
            loading="eager"
            decoding="async"
            data-testid="video-poster"
          />
        ) : (
          <GeneratedThumb seedKey={video.id} fill />
        )}
      </Art>
      <Corner $side="left">
        <Pill>
          <Icon name="crown" size={15} />
          Most Featured
        </Pill>
        {video.sample && <Pill data-testid="sample-badge">Sample</Pill>}
      </Corner>
      <Corner $side="right">
        <Duration>{formatDuration(video.durationSec)}</Duration>
      </Corner>
      <PlayButton aria-hidden="true">
        <Icon name="play" size={28} />
      </PlayButton>

      <Body>
        {video.category && (
          <Eyebrow>{video.category.charAt(0).toUpperCase() + video.category.slice(1)}</Eyebrow>
        )}
        <Title>
          {lead && <>{lead} </>}
          <Accent>{last}</Accent>
        </Title>
        {reason && <Reason data-testid="featured-reason">{reason}</Reason>}
        {creator && (
          <CreatorRow>
            <Avatar name={creator.name} avatarKey={creator.av} size={36} />
            <CreatorText>
              <CreatorName>
                {creator.name}
                {creator.verified && (
                  <Verified>
                    <Icon name="verify" size={14} />
                  </Verified>
                )}
              </CreatorName>
              <Small>
                <span>{creator.handle}</span>
                <span>{video.ageLabel}</span>
              </Small>
            </CreatorText>
          </CreatorRow>
        )}
        {video.description && <Blurb>{video.description}</Blurb>}
        <Stats>
          <span>
            <Icon name="eye" size={16} />
            {formatViews(video.views)}
          </span>
          {likes !== undefined && (
            <span>
              <Icon name="cherish" size={16} />
              {formatCount(likes)}
            </span>
          )}
          {comments !== undefined && (
            <span>
              <Icon name="comment" size={16} />
              {formatCount(comments)}
            </span>
          )}
          {menu && <MenuSlot>{menu}</MenuSlot>}
        </Stats>
      </Body>
    </Hero>
  );
}
